import React from "react";
import { Stack, Typography, IconButton } from "@mui/material";
import { useWatch } from "react-hook-form";
import { Edit as EditIcon } from "components/icon/mui_icon";
import { formatBytes } from "./utils/formatBytes";
import { StyledFormLabels, StyledInputGrid } from "customStyled";
import { DynamicAvatar } from "Reuse/UI";
import { UploadIcon } from "components/icon";
import { getImageExtensionFromBase64, setImageData } from "./utils";
import { isValidValue } from "utils";
import { useDialog } from "contexts/DialogContext";
import { useTranslation } from "react-i18next";
import i18n from "i18n";

const acceptFormat = {
  svg: "image/svg",
  gif: "image/gif",
  png: "image/x-png, image/png",
  jpg: "image/jpg, image/jpeg",
};

const imgUpload = (event, form, keyName = "image", cond = null, showError) => {
  if (!event.target.files.length) return;

  const file = event.target.files[0];
  const reader = new FileReader();

  reader.onload = () => {
    const img = new Image();
    img.src = reader.result;

    img.onload = () => {
      // Check dimension (not greater than 800x400)
      if (!!cond && !!cond.width && !!cond.height) {
        if (img.width > cond.width || img.height > cond.height) {
          if (!!showError) {
            showError(
              i18n.t("form:imageTooLarge", {
                width: cond.width,
                height: cond.height,
              }),
            );
          } else {
            alert(
              i18n.t("form:imageTooLarge", {
                width: cond.width,
                height: cond.height,
              }),
            );
          }
          return;
        }
      }

      const imgSrc = reader.result;
      const imgSrcExt = getImageExtensionFromBase64(imgSrc);

      setImageData(
        { file },
        { value: file, imgSrc, imgSrcExt, crop: false },
        form,
        keyName,
      );
    };

    img.onerror = () => {
      if (!!showError) {
        showError(i18n.t("form:invalidImageFile"));
      } else {
        alert(i18n.t("form:invalidImageFile"));
      }
    };
  };

  reader.readAsDataURL(file);
  event.target.value = null;
};

const FileUpload = (props) => {
  const {
    title = "Upload File",
    optional = true,
    accept = ["svg", "gif", "png", "jpg"],
    acceptText = "SVG, PNG, JPG or GIF",
    form,
    keyName = "logo",
    cond = null,
  } = props;
  const { t } = useTranslation("form");
  const { showError } = useDialog();
  const acceptType = accept.map((ext) => acceptFormat[ext]).join(", ");
  const onPick = (e) => imgUpload(e, form, keyName, cond, showError);

  return (
    <StyledInputGrid>
      <StyledFormLabels htmlFor="file-upload">
        <Stack direction="row" sx={{ gap: "12px" }}>
          {title}
          <Typography variant="h6" sx={{ color: "#70737B" }}>
            {optional ? t("optional") : null}
          </Typography>
        </Stack>
      </StyledFormLabels>
      <FormFileUploadBox
        form={form}
        keyName={keyName}
        acceptType={acceptType}
        acceptText={acceptText}
        onPick={onPick}
      />
    </StyledInputGrid>
  );
};

// The image group lives at `keyName` (see imageDefaults)
const FormFileUploadBox = ({ form, keyName, ...rest }) => {
  const value = useWatch({ control: form.control, name: keyName }) ?? {};
  // read both every render (react-hook-form only tracks state that is read)
  const { isTouched } = form.getFieldState(keyName, form.formState);
  const { isSubmitted } = form.formState;
  const touched = isTouched || isSubmitted;
  return (
    <FileUploadBox
      value={value}
      invalid={!form.isPathValid(keyName)}
      touched={touched}
      {...rest}
    />
  );
};

const FileUploadBox = ({
  value,
  invalid,
  touched,
  acceptType,
  acceptText,
  onPick,
}) => {
  const { t } = useTranslation("form");
  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        border: `1px solid ${invalid && touched ? "#ff4d4f" : "#DBDDE0"}`,
        borderRadius: 2,
        padding: 2,
        minHeight: 120,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {!isValidValue(value.value) ? (
        <>
          <input
            // named by the field's label (htmlFor="file-upload")
            id="file-upload"
            type="file"
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              opacity: 0,
              cursor: "pointer",
            }}
            accept={acceptType}
            onChange={onPick}
          />
          <UploadIcon />
          <Typography>
            <b>{t("clickToUpload")}</b> {t("orDragAndDrop")}
          </Typography>
          <Typography sx={{ letterSpacing: 1.25 }}>{acceptText}</Typography>
        </>
      ) : (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {value.upload ? (
            <DynamicAvatar src={value.thumb || ""} sizes="small" />
          ) : (
            <DynamicAvatar src={value.value || ""} sizes="small" />
          )}
          <Stack sx={{ flexGrow: 1 }}>
            <Typography>
              {value.upload ? value.value.name : value.value.split("/").pop()}
            </Typography>
            <Typography>
              {value.upload ? formatBytes(value.value.size) : null}
            </Typography>
          </Stack>
          <IconButton component="label" aria-label={t("changeImage")}>
            <EditIcon />
            <input
              type="file"
              style={{ display: "none" }}
              accept={acceptType}
              onChange={onPick}
            />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};

export default FileUpload;
